<script setup lang="ts">
import { computed } from 'vue';
import { IonIcon } from '@ionic/vue';
import { closeOutline, refreshOutline } from 'ionicons/icons';
import { useI18n } from '@/i18n';
import { useMobileHealthStore } from '@/stores/useMobileHealthStore';

type HealthBanner = {
  key: 'network' | 'location' | 'location-address';
  // 'warning' = avviso giallo con pulsante "riprova"; 'info' = banner informativo
  // (posizione da indirizzo profilo) con pulsante di chiusura.
  tone: 'warning' | 'info';
  message: string;
  busy: boolean;
  refresh?: () => void;
  dismiss?: () => void;
};

const props = withDefaults(defineProps<{
  showNetwork?: boolean;
  showLocation?: boolean;
}>(), {
  showNetwork: true,
  showLocation: true,
});

const { t } = useI18n();
const mobileHealth = useMobileHealthStore();

const banners = computed<HealthBanner[]>(() => {
  const items: HealthBanner[] = [];
  if (props.showNetwork && mobileHealth.showNetworkBanner) {
    items.push({
      key: 'network',
      tone: 'warning',
      message: t('mobile_no_internet'),
      busy: mobileHealth.checkingNetwork,
      refresh: () => { void mobileHealth.checkNetwork(); },
    });
  }
  if (props.showLocation && mobileHealth.showLocationBanner) {
    items.push({
      key: 'location',
      tone: 'warning',
      message: t('mobile_location_unavailable_no_address'),
      busy: mobileHealth.checkingLocation,
      refresh: () => { void mobileHealth.checkLocation({ requestPermission: true }); },
    });
  }
  if (props.showLocation && mobileHealth.showLocationAddressBanner) {
    items.push({
      key: 'location-address',
      tone: 'info',
      message: t('mobile_location_using_profile_address'),
      busy: false,
      dismiss: () => { mobileHealth.dismissAddressBanner(); },
    });
  }
  return items;
});
</script>

<template>
  <Teleport to="body">
    <TransitionGroup
      v-if="banners.length"
      name="mobile-health"
      tag="div"
      class="mobile-health-stack"
      aria-live="polite"
    >
      <div
        v-for="banner in banners"
        :key="banner.key"
        class="mobile-health-bar"
        :class="`mobile-health-bar--${banner.tone}`"
        role="status"
      >
        <span class="mobile-health-text">{{ banner.message }}</span>
        <button
          v-if="banner.refresh"
          type="button"
          class="mobile-health-action"
          :aria-label="t('mobile_health_refresh_aria')"
          @click="banner.refresh"
        >
          <ion-icon :icon="refreshOutline" :class="{ spinning: banner.busy }" />
        </button>
        <button
          v-else-if="banner.dismiss"
          type="button"
          class="mobile-health-action"
          :aria-label="t('mobile_health_dismiss_aria')"
          @click="banner.dismiss"
        >
          <ion-icon :icon="closeOutline" />
        </button>
      </div>
    </TransitionGroup>
  </Teleport>
</template>

<style scoped>
.mobile-health-stack {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 90000;
  display: flex;
  flex-direction: column;
  padding-top: env(safe-area-inset-top, 0px);
  pointer-events: none;
}

.mobile-health-bar {
  width: 100%;
  min-height: 28px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 30px;
  align-items: center;
  gap: 4px;
  padding: 3px 6px 3px 10px;
  border-bottom: 1px solid rgba(31, 41, 55, 0.18);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.2);
  pointer-events: auto;
}

/* Avviso (rete assente / GPS spento senza indirizzo): giallo. */
.mobile-health-bar--warning {
  background: #facc15;
  color: #1f2937;
}

/* Informativo (GPS spento ma posizione presa dall'indirizzo del profilo):
   azzurro tenue, per distinguerlo dall'avviso giallo. */
.mobile-health-bar--info {
  background: #bae6fd;
  color: #0c4a6e;
  border-bottom-color: rgba(12, 74, 110, 0.18);
}

.mobile-health-text {
  min-width: 0;
  font-size: 10.5px;
  font-weight: 800;
  line-height: 1.15;
}

.mobile-health-action {
  width: 28px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: rgba(31, 41, 55, 0.12);
  color: inherit;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
}

.mobile-health-bar--info .mobile-health-action {
  background: rgba(12, 74, 110, 0.12);
}

.mobile-health-action ion-icon {
  font-size: 16px;
}

.mobile-health-action:active {
  transform: scale(0.96);
}

.spinning {
  animation: health-refresh-spin 0.8s linear infinite;
}

.mobile-health-enter-active,
.mobile-health-leave-active {
  transition:
    transform var(--md-duration-short4, 180ms) var(--md-easing-standard, ease),
    opacity var(--md-duration-short4, 180ms) var(--md-easing-standard, ease);
}

.mobile-health-enter-from,
.mobile-health-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

@keyframes health-refresh-spin {
  to { transform: rotate(360deg); }
}
</style>
